import {
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  Input,
  Typography,
} from "@material-ui/core"
import { AddCircle } from "@material-ui/icons"
import RemoveCircleIcon from "@material-ui/icons/RemoveCircle"
import classnames from "clsx"
import { useFormik } from "formik"
import { isEmpty, orderBy } from "lodash"
import PropTypes from "prop-types"
import { useCallback, useContext, useEffect, useState } from "react"
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd"
import * as Yup from "yup"
import dragGray from "~/assets/Icons/noun_drag_3124730_gray.svg"
import DarkButton from "~/components/Common/DarkButton"
import AppContext from "~/contexts/App"
import LabContext from "~/contexts/Lab"

import Box from "~/components/Common/Box"
import PermissionsWrapper from "~/components/Common/PermissionsWrapper"

import useStyles from "./styles"

const validationSchema = Yup.object().shape({
  name: Yup.string().required("A name is required"),
  port: Yup.number()
    .typeError("Port should be a number")
    .positive("Port should be a positive number")
    .required("A port is required"),
})

const TabCard = ({ data = {}, instances = [], index, disabled = false }) => {
  const classes = useStyles()

  const { removePort, updatePort } = useContext(LabContext)
  const [removing, setRemoving] = useState(false)
  const { openSnackBar, openDialog } = useContext(AppContext)
  const [isEdit, setIsEdit] = useState(false)

  const handleToggleEdit = useCallback(() => {
    setIsEdit(!isEdit)
  }, [isEdit])

  const handleRemovePort = useCallback(async () => {
    try {
      setRemoving(true)
      await removePort(data?.id)
    } catch (e) {
      setRemoving(false)
      openSnackBar({
        message: "There was an error removing this port",
      })
    }
  }, [data?.id, removePort])

  const handleUpdatePort = useCallback(
    async (values, actions) => {
      try {
        await updatePort(data?.id, values)
        openSnackBar({
          message: "Port has been updated",
        })
        setIsEdit(false)
      } catch (e) {
        openSnackBar({
          message: "There was an error updating this port",
        })
        actions.resetForm()
      }
    },
    [data?.id, updatePort],
  )

  const {
    setValues,
    submitForm,
    isSubmitting,
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
  } = useFormik({
    initialValues: {
      name: "",
      port: "",
      check_before_ready: false,
    },
    validationSchema,
    onSubmit: handleUpdatePort,
  })

  useEffect(() => {
    setValues(
      {
        name: data.name,
        port: data.port,
        check_before_ready: Boolean(data.check_before_ready),
      },
      false,
    )
  }, [data.name, data.port, data.check_before_ready])

  const openEditDialog = (data, instances) => {
    openDialog("editLabOutputDialog", { data, instances })
  }

  return (
    <Draggable draggableId={data.id.toString()} index={index}>
      {(provided) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={classnames(classes.tabCard, {
            [classes.isEdit]: isEdit,
          })}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexGrow="1"
            onClick={() => openEditDialog(data, instances)}
          >
            <Box width="36%">
              {/* {isEdit && (
                <FormControl
                  error={touched.name && errors.name}
                  fullWidth
                  style={{ marginTop: 4 }}
                >
                  <Input
                    name="name"
                    placeholder="Name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                  />
                  <FormHelperText>{errors?.name || " "}</FormHelperText>
                </FormControl>
              )} */}
              {!isEdit && <Typography>{data?.name}</Typography>}
            </Box>
            <Box width="20%">
              {isEdit && (
                <FormControl
                  error={touched.port && errors.port}
                  fullWidth
                  style={{ marginTop: 4 }}
                >
                  <Input
                    name="port"
                    placeholder="Port"
                    value={values.port}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                  />
                  <FormHelperText>{errors?.port || " "}</FormHelperText>
                </FormControl>
              )}
              {!isEdit && <Typography>{data?.port}</Typography>}
            </Box>
            <Box width="20%">
              <Typography>
                {instances?.map((instance) =>
                  instance.id === data?.lab_instance_id ? instance.name : "",
                )}
              </Typography>
            </Box>
            {/* <Box width="36%">
              {isEdit && (
                <FormControlLabel
                  control={
                    <Checkbox
                      name="check_before_ready"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      checked={values.check_before_ready}
                      value={values.check_before_ready}
                      size="small"
                    />
                  }
                  label="Check Before Ready"
                />
              )}
              {!isEdit && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.check_before_ready}
                      value={values.check_before_ready}
                      size="small"
                      disableRipple
                      style={{ cursor: "default" }}
                    />
                  }
                  style={{ cursor: "default" }}
                  label="Check Before Ready"
                />
              )}
            </Box> */}
          </Box>
          {/* {isEdit && (
            <Box minWidth={65} display="flex" justifyContent="flex-end">
              <LoaderButton
                style={{
                  flexShrink: 0,
                  flexGrow: 0,
                }}
                color="secondary"
                size="small"
                onClick={submitForm}
                working={isSubmitting}
              >
                Save
              </LoaderButton>
            </Box>
          )} */}
          {!isEdit && (
            <Box
              display="flex"
              flexShrink="0"
              flexGrow="0"
              visibility={disabled ? "hidden" : "visible"}
            >
              {/* <IconButton
                size="small"
                onClick={handleToggleEdit}
                disabled={removing}
              >
                <EditIcon style={{ fontSize: "inherit" }} />
              </IconButton> */}
              <IconButton
                size="small"
                className={classes.removeButton}
                onClick={handleRemovePort}
                disabled={removing}
              >
                {removing && (
                  <CircularProgress
                    size={12}
                    style={{ position: "absolute" }}
                  />
                )}
                <RemoveCircleIcon style={{ fontSize: "inherit" }} />
              </IconButton>
              <div className={classes.dragHandle} {...provided.dragHandleProps}>
                <img src={dragGray} alt="" width="11px" />
              </div>
            </Box>
          )}
        </Box>
      )}
    </Draggable>
  )
}

TabCard.propTypes = {
  data: PropTypes.object,
  index: PropTypes.number,
  disabled: PropTypes.bool,
  instances: PropTypes.array,
}

const TabsBuilder = ({ ports = [], disabled = false, instances = [] }) => {
  const classes = useStyles()

  const [orderedPorts, setOrderedPorts] = useState([])

  useEffect(() => {
    if (!isEmpty(ports)) {
      setOrderedPorts(orderBy(ports, ["order"], ["asc"]))
    }
  }, [ports])

  // const [checked, setChecked] = useState(false)

  // const handleChange = (e) => setChecked(e.target.checked)

  const { createPort, orderPorts, lab } = useContext(LabContext)
  const { openDialog } = useContext(AppContext)

  const openCreateDialog = () => {
    openDialog("createTabDialog", { lab })
  }

  const handleCreatePort = useCallback(
    async (values, actions) => {
      await createPort({
        name: values.name,
        port: values.port + "", // API expects string
        check_before_ready: values.check_before_ready,
      })
      actions.resetForm()
      // actions.setSubmitting(false)
    },
    [createPort],
  )

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
  }

  const handleOnDragEnd = async (result) => {
    // dropped outside the list
    if (!result.destination) {
      return
    }

    const currentPorts = Array.from(orderedPorts)

    const newPorts = reorder(
      orderedPorts,
      result.source.index,
      result.destination.index,
    )

    setOrderedPorts(newPorts)

    try {
      await orderPorts(newPorts.map((p) => p.id))
    } catch (e) {
      console.error(e)
      setOrderedPorts(currentPorts)
    }
  }

  return (
    <Box paddingTop="16px" maxWidth="750px">
      {/* <Box
        display="flex"
        alignItems="center"
        marginBottom="12px"
        justifyContent="space-between"
        height="36px"
      >
        <Typography
          variant="subtitle1"
          style={{ fontWeight: 600 }}
          color="secondary"
        >
          Enable Code Editor
        </Typography>
        <Switch
          checked={checked}
          onChange={handleChange}
          color="primary"
          name="enable-code-editor"
          inputProps={{ "aria-label": "enable code editor" }}
        />
      </Box> */}
      <Box
        display="flex"
        justifyContent="space-between"
        height="40px"
        marginBottom="24px"
      >
        <Typography
          variant="subtitle1"
          style={{
            fontWeight: 600,
            fontSize: "1.125rem",
            marginRight: "160px",
          }}
          color="primary"
        >
          Lab Output Tabs
        </Typography>
        <PermissionsWrapper permissionGroup="admin">
          <DarkButton
            onClick={openCreateDialog}
            startIcon={<AddCircle fontSize="small" />}
          >
            Add Output Tab
          </DarkButton>
        </PermissionsWrapper>
      </Box>
      {/* {!disabled && (
        <>
          <Typography variant="body2" style={{ marginTop: 4 }}>
            What ports would you like your users to interact on?
          </Typography>
          <Formik
            initialValues={{
              name: "",
              port: "",
              check_before_ready: false,
            }}
            validationSchema={validationSchema}
            onSubmit={handleCreatePort}
          >
            {({ isSubmitting, values }) => (
              <Form>
                <Box
                  width="88%"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  marginTop="8px"
                >
                  <Box
                    flexGrow={1}
                    display="flex"
                    justifyContent="space-between"
                    paddingRight={1}
                    marginRight="8px"
                  >
                    <Field
                      component={TextField}
                      placeholder="Name"
                      name="name"
                      color="secondary"
                      style={{ width: "62%" }}
                    />
                    <Field
                      component={TextField}
                      placeholder="Port"
                      name="port"
                      color="secondary"
                      style={{ width: "32%" }}
                    />
                  </Box>
                  <Box>
                    <Field
                      component={CheckboxWithLabel}
                      value={values.check_before_ready}
                      checked={values.check_before_ready}
                      name="check_before_ready"
                      color="secondary"
                      size="small"
                      Label={{ label: "Check Before Ready" }}
                      style={{ marginLeft: "8px" }}
                    />
                  </Box>
                  <LoaderButton
                    style={{
                      flexShrink: 0,
                      flexGrow: 0,
                    }}
                    color="secondary"
                    startIcon={<AddCircleIcon />}
                    size="small"
                    type="submit"
                    working={isSubmitting}
                  >
                    Add
                  </LoaderButton>
                </Box>
              </Form>
            )}
          </Formik>
        </>
      )}
      {ports.length > 0 && (

      )} */}
      <Box paddingTop="13px">
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="list">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {orderedPorts.map((tab, idx) => (
                  <TabCard
                    key={tab.id}
                    data={tab}
                    instances={instances}
                    index={idx}
                    disabled={disabled}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    </Box>
  )
}

TabsBuilder.propTypes = {
  ports: PropTypes.array,
  disabled: PropTypes.bool,
  instances: PropTypes.array,
}

export default TabsBuilder
